/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
import { arrayOf, func, string, oneOfType, object, number } from 'prop-types';

import {
  formatDollarAmount,
  formatPercentAmount,
  plusMinusValue
} from 'services/Formats';

import { isPositiveValue } from 'services/Calculation';

/**
 * A low level component to create common Text types
 * @param formatters - a list of formatter functions which turn any data into a string
 * @param textString - text that will be rendered
 * @param className - a deliberate list of classes appart from the ones added by boolean props ( baseStyleAttributes )
 *                    this list will override any of the boolean props classes and will be overwritten by a `style` prop
 * @param element - an optional string to define the type of HTML element Text should render
 * @param props - An object of all other props which include boolean prop classes
 * @returns {*}
 * @constructor
 *
 * @example
 * <Text>example</Text> -> <span>example</span>
 *
 * <Text green light>example</Text> -> <span className='green light'>example</span>
 *
 * const Header = props => <Text element='h1' bold large {...props}>
 * <Header>example</Example> -> <h1 className='bold large'>example</h1>
 *
 * const SlashDate = props => <Text formatters={ [ momentFormater ] } {...props} />
 * <SlashDate light ... /> -> <span className="light">6/7/2018</span>
 */

export default function Text({
  classFormatters = [],
  formatters = [],
  children = '',
  className = '',
  element: El = 'span',
  ...props
}) {
  const textString = Array.isArray(children) ? children.join('') : children;
  const classNames =
    classFormatters
      .map((formatter) => formatter(textString))
      .filter((v) => v)
      .join(' ') +
    ' ' +
    (className ? className : '');

  return (
    <El className={classNames} {...props}>
      {formatters
        .reverse()
        .reduce((str, formatter) => formatter(str), textString)}
    </El>
  );
}

Text.propTypes = {
  formatters: arrayOf(func),
  children: oneOfType([number, string, arrayOf(string), object]),
  className: string,
  element: string,
  props: object
};

export const FormattedDate = ({
  formatters = [],
  format = 'MM/DD/Y',
  ...props
}) => (
  <Text
    formatters={formatters.concat((str) =>
      str ? moment.utc(str).format(format) : null
    )}
    {...props}
  />
);

export const FormattedTime = ({
  formatters = [],
  format = 'hh:mma z',
  ...props
}) => (
  <Text
    formatters={formatters.concat((str) =>
      str ? moment(str).format(format) : null
    )}
    {...props}
  />
);

export const FormattedDateTime = ({
  formatters = [],
  format = 'MM/DD/Y hh:mm A',
  ...props
}) => (
  <Text
    formatters={formatters.concat((str) =>
      str ? moment(str).format(format) + ' ET' : null
    )}
    {...props}
  />
);

export const Dollar = ({ formatters = [], format = '$0,0.00', ...props }) => (
  <Text
    formatters={formatters.concat((str) => formatDollarAmount(str, format))}
    {...props}
  />
);

// const evaluateNumber = val => numeral(val).value() > 0;
// const formatWithEval = val => evaluateNumber( val ) ? '+' + val : val;
const formatWithArrow = (val) =>
  !val || val === 0
    ? 'no-value'
    : isPositiveValue(val)
    ? 'positive-value'
    : 'negative-value';

export const EvaluatedDollar = ({
  formatters = [plusMinusValue],
  format,
  ...props
}) => <Dollar formatters={formatters} format={format} {...props} />;

export const Percent = ({ formatters = [], withDecimal = true, ...props }) => (
  <Text
    formatters={formatters.concat((str) =>
      formatPercentAmount(str, withDecimal)
    )}
    {...props}
  />
);

export const EvaluatedPercent = ({
  formatters = [plusMinusValue],
  withArrow,
  ...props
}) => (
  <Percent
    classFormatters={withArrow ? [formatWithArrow] : []}
    formatters={formatters}
    {...props}
  />
);

export const Quantity = ({
  extraDecimal = false,
  formatters = [],
  ...props
}) => (
  <Text
    formatters={formatters.concat((str) =>
      numeral(str).format('0,0' + (extraDecimal ? '.0000' : '.000'))
    )}
    {...props}
  />
);

export const Shares = ({ formatters = [], ...props }) => (
  <Quantity
    formatters={formatters.concat((str) => str + ' Shares')}
    {...props}
  />
);
